import clsx from "clsx";

import classes from "./styles.module.scss";

import type {
  IAppointmentDataStatus,
  IAppointmentStatus,
} from "@components/molecules/appointmentStatus";
import { AppointmentNoShows } from "@components/organisms/appointmentCard/components/appointmentNoShows";
import type {
  IAppointmentCardBottomProps,
  IAppointmentCardVariants,
  IAppointmentDataVariants,
  IMspVariants,
} from "@components/organisms/appointmentCard/components/appointmentCardBottom/types";
import {
  AppointmentDataStatusPlace,
  AppointmentStatusPlace,
} from "@components/organisms/appointmentCard/components/appointmentStatusPlace";
import {
  AppointmentCardButtons,
  AppointmentDataCardButtons,
  PastAppointmentCardButtons,
  SummaryCardButtons,
} from "@components/organisms/appointmentCard/components/appointmentCardButtons";

/**
 * AppointmentCardBottom component displays the bottom section of an appointment card.
 *
 * @param {IAppointmentCardBottomProps} props - The props for the AppointmentCardBottom component.
 * @param {IAppointmentStatus} props.status - The status of the appointment.
 * @returns AppointmentCardBottom The rendered AppointmentCardBottom component.
 */
export function AppointmentCardBottom({
  status,
  canCancel,
  canJoin,
  variant,
  canWriteAddendum,
  canWriteSummary,
  canViewSummary,
  canViewDetail,
  canWriteSummaryToAppointment,
  canDecline,
  canAccept,
  canViewRemainingTime,
  startDate,
  canNoShow,
  id,
  noShowCause,
  canWriteComment,
  appointmentType,
  summaryNoteId,
  visitType,
  doctorName,
  canEditIntakeForm,
  onEditIntakeForm,
  noShowType,
  noShowReason,
}: IAppointmentCardBottomProps) {
  const appointmentData: IAppointmentCardVariants[] = [
    "followup",
    "orders",
    "referral",
    "prescription",
    "images",
    "experiments",
  ];

  const msp: IAppointmentCardVariants[] = [
    "msp_prescription",
    "msp_image",
    "msp_order",
  ];

  const noStatus: IAppointmentCardVariants[] = [
    "past-appointment",
    "summary-note",
    "intake-form",
  ];

  const isAppointmentData = appointmentData?.includes(variant);
  const isNoStatus = noStatus?.includes(variant);
  const isMsp = msp?.includes(variant);

  return (
    <div
      className={clsx(classes.bottom, isNoStatus && classes.bottom__noStats)}
    >
      {variant === "no-show" && (
        <AppointmentNoShows
          noShowCause={noShowCause}
          id={id}
          visitType={visitType}
          noShowType={noShowType}
        />
      )}
      {/* Render the status of the appointment */}
      {variant === "appointment" && status && (
        <AppointmentStatusPlace
          status={status as IAppointmentStatus}
          isCard
          noShowCause={noShowCause}
          noShowReason={noShowReason}
        />
      )}
      {((isAppointmentData && variant === "followup") || isMsp) && (
        <AppointmentDataStatusPlace
          status={status as IAppointmentDataStatus}
          isMsp={isMsp}
          mspType={variant as IMspVariants}
        />
      )}
      {/* Render the buttons for the appointment card */}
      {variant === "appointment" && (
        <AppointmentCardButtons
          canJoin={canJoin}
          canCancel={canCancel}
          canViewDetail={canViewDetail}
          canViewSummary={canViewSummary}
          canWriteSummaryToAppointment={canWriteSummaryToAppointment}
          canDecline={canDecline}
          canAccept={canAccept}
          canViewRemainingTime={canViewRemainingTime}
          startDate={startDate}
          canNoShow={canNoShow}
          id={id}
          canWriteComment={canWriteComment}
          appointmentType={appointmentType}
          summaryNoteId={summaryNoteId}
          doctorName={doctorName}
        />
      )}
      {variant === "intake-form" && (
        <AppointmentCardButtons
          appointmentType={appointmentType}
          canJoin={false}
          canCancel
          cancelDisabled={!canCancel}
          canViewDetail={false}
          canViewSummary={false}
          canWriteSummaryToAppointment={false}
          canDecline={false}
          canAccept={false}
          canViewRemainingTime={false}
          startDate={startDate}
          id={id}
          summaryNoteId={summaryNoteId}
          doctorName={doctorName}
          canViewIntakeForm
          canEditIntakeForm={canEditIntakeForm}
          onEditIntakeForm={onEditIntakeForm}
        />
      )}
      {(isAppointmentData || isMsp) && (
        <AppointmentDataCardButtons
          type={variant as IAppointmentDataVariants}
          id={id}
          summaryNoteId={summaryNoteId}
        />
      )}
      {variant === "past-appointment" && (
        <PastAppointmentCardButtons id={summaryNoteId} />
      )}
      {variant === "summary-note" && (
        <SummaryCardButtons
          canWriteAddendum={canWriteAddendum}
          canWriteSummary={canWriteSummary}
          id={id}
          summaryNoteId={summaryNoteId}
        />
      )}
    </div>
  );
}
